import { motionHaptic, type MotionToken } from "@oshimap/design-tokens";
import { stableRotation, ROTATION_MAX } from "@oshimap/utils";
import { prefersReducedMotion } from "./reduced-motion";

/** Web 에는 Haptic 이 없다. 계약만 공유하고 Native 에서 구현한다 (MI-2). */
export function haptic(token: MotionToken): void {
  const kind = motionHaptic[token];
  if (!kind) return;
  if (typeof navigator !== "undefined" && "vibrate" in navigator && kind === "heavy") {
    try { navigator.vibrate?.(12); } catch { /* 지원하지 않으면 조용히 넘어간다 */ }
  }
}

export function play(el: HTMLElement | null, cls: string): void {
  if (!el || prefersReducedMotion()) return;
  el.classList.remove(cls);
  void el.offsetWidth;
  el.classList.add(cls);
  el.addEventListener("animationend", () => el.classList.remove(cls), { once: true });
}

const STAR = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 1.8 14.4 9 21.8 9 15.8 13.5 18.1 20.8 12 16.3 5.9 20.8 8.2 13.5 2.2 9 9.6 9z"/></svg>';

/**
 * SPARKLE. Reduced Motion 에서는 아무것도 그리지 않고 호출부가 정적 대체를 담당한다.
 *
 * `delay` 는 CSS animation-delay 로 준다. setTimeout 으로 순서를 짜면 탭이
 * 뒤에 있을 때 타이머가 늦춰져 연출이 통째로 어긋난다 (실측: 숨은 탭에서 1s 로 밀린다).
 */
export function sparkle(host: HTMLElement | null, count = 5, delay = 0): void {
  if (!host || prefersReducedMotion()) return;
  if (getComputedStyle(host).position === "static") host.style.position = "relative";
  const tones = ["var(--yellow-300)", "var(--pink-400)", "var(--purple-300)"];
  for (let i = 0; i < count; i += 1) {
    const s = document.createElement("span");
    s.setAttribute("aria-hidden", "true");
    s.style.cssText = `position:absolute;top:50%;left:50%;width:9px;height:9px;pointer-events:none;z-index:9;
      color:${tones[i % tones.length]};animation:om-sparkle 500ms var(--ease-standard) forwards;
      animation-delay:${delay + i * 42}ms;--dx:${(Math.random() * 54 - 27).toFixed(1)}px;--dy:${(-16 - Math.random() * 30).toFixed(1)}px`;
    s.innerHTML = STAR;
    host.appendChild(s);
    window.setTimeout(() => s.remove(), delay + 760 + i * 42);
  }
}

/**
 * STAMP — 제품의 시그니처 모션 (15 §3.3). 방문이 남는 순간 반드시 일어난다.
 *
 * 각도는 고정 시드다. 같은 스탬프는 몇 번을 봐도 같은 각으로 기운다 —
 * 매번 달라지면 "찍힌 자국" 이 아니라 그냥 흔들리는 그림이 된다.
 *
 * Reduced Motion 에서는 CSS 가 회전 없는 페이드 인으로 바꾼다. 여기서 막지 않는 이유는
 * 스탬프가 **일어났다는 사실 자체**는 알려야 하기 때문이다 (없애는 게 아니라 바꾸는 것).
 */
export function stamp(el: HTMLElement | null, seed: string): void {
  if (!el) return;
  el.style.setProperty("--rot", `${stableRotation(seed, ROTATION_MAX.stamp).toFixed(2)}deg`);

  if (!prefersReducedMotion()) {
    /* 잉크 번짐. 스탬프 뒤에서 한 번 퍼진다. */
    if (getComputedStyle(el).position === "static") el.style.position = "relative";
    const ink = document.createElement("span");
    ink.className = "om-ink";
    ink.setAttribute("aria-hidden", "true");
    el.appendChild(ink);
    window.setTimeout(() => ink.remove(), 420);
  }

  el.classList.remove("mo-stamp");
  void el.offsetWidth;
  el.classList.add("mo-stamp");
  haptic("STAMP");
}

/** WIGGLE — 1회만. 반복 금지 (15 §3.5). Reduced Motion 에서는 아무 일도 하지 않는다. */
export function wiggle(el: HTMLElement | null): void {
  if (!el || prefersReducedMotion()) return;
  play(el, "mo-wiggle");
}

/** FLIP — 포토카드/컬렉션 (15 §3.6). Reduced Motion 에서는 CSS 가 크로스페이드로 바꾼다. */
export function flip(el: HTMLElement | null): void {
  if (!el) return;
  el.classList.remove("mo-flip");
  void el.offsetWidth;
  el.classList.add("mo-flip");
  el.addEventListener("animationend", () => el.classList.remove("mo-flip"), { once: true });
  haptic("FLIP");
}

const CONFETTI_TONES = ["var(--purple-500)", "var(--pink-400)", "var(--yellow-300)"];

/**
 * CONFETTI — 강한 Reward (15 §3.7). Collection Complete · Milestone · Achievement.
 *
 * Reduced Motion 에서는 파티클을 만들지 않는다. 대신 **사라지지 않는 정적 축하**를
 * 호출부가 보여줘야 한다 — 이 함수는 그 사실을 반환값으로 알린다.
 * 움직임을 못 보는 사람에게 축하가 통째로 없어지면 안 된다.
 */
export function confetti(host: HTMLElement | null, count = 32, delay = 0): boolean {
  if (!host) return false;
  if (prefersReducedMotion()) return false;
  if (getComputedStyle(host).position === "static") host.style.position = "relative";

  for (let i = 0; i < count; i += 1) {
    const p = document.createElement("span");
    p.setAttribute("aria-hidden", "true");
    const angle = (Math.PI * (0.15 + Math.random() * 0.7)) - Math.PI;   /* 위쪽으로 퍼진다 */
    const dist = 60 + Math.random() * 120;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist + 140;                            /* 그러다 떨어진다 */
    p.style.cssText = `position:absolute;left:50%;top:44%;width:${6 + Math.random() * 5}px;
      height:${9 + Math.random() * 6}px;border-radius:2px;pointer-events:none;z-index:9;
      background:${CONFETTI_TONES[i % CONFETTI_TONES.length]};
      animation:om-confetti 700ms var(--ease-standard) forwards;animation-delay:${delay + i * 6}ms;
      --dx:${dx.toFixed(1)}px;--dy:${dy.toFixed(1)}px;--spin:${(Math.random() * 720 - 360).toFixed(0)}deg`;
    host.appendChild(p);
    window.setTimeout(() => p.remove(), delay + 900 + i * 6);
  }
  haptic("CONFETTI");
  return true;
}
